import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { EvidenceBase, evidenceKeys, useProjectDocuments, type EvidenceDocument } from "@lumanity/ui/evidence";
import { useProject } from "@lumanity/ui/projects";
import { Button, ErrorBanner, toast } from "@lumanity/ui/primitives";
import { useDocumentViewer } from "@lumanity/ui/viewer";
import { PROJECT_OWNERSHIP_CONFIG } from "./uploadModel";

interface ProjectEvidenceBaseProps {
  projectId: string;
  /** Go to the Upload step. */
  onUpload?: () => void;
  /** Extra toolbar buttons, e.g. the stepper's Continue. */
  toolbarActions?: ReactNode;
}

/**
 * The Evidence Base step: the project's documents in @lumanity/ui's
 * EvidenceBase (edit metadata, view, remove). As in FRaiME, indexing is queued
 * by the Upload step and runs in the background. Needs `EvidenceViewerProvider` above it.
 */
const ProjectEvidenceBase = ({ projectId, onUpload, toolbarActions }: Readonly<ProjectEvidenceBaseProps>) => {
  const queryClient = useQueryClient();
  const { data: project } = useProject(projectId);
  const { data: projectDocs, isLoading, error } = useProjectDocuments(projectId);
  const { openDocument } = useDocumentViewer<EvidenceDocument>();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: evidenceKeys.all });

  if (error) return <ErrorBanner message={`Could not load the project's documents: ${error.message}`} />;
  if (isLoading) return <div className="text-muted-foreground py-8 text-center">Loading documents...</div>;

  const canEdit = project?.my_role !== "viewer";
  const projectModel = typeof project?.properties?.model === "string" ? project.properties.model : undefined;
  const hasDocuments = (projectDocs?.documentIds.length ?? 0) > 0;

  if (!hasDocuments) {
    return (
      <div className="rounded-lg border bg-card py-10 text-center text-muted-foreground space-y-3">
        <p>No documents have been added to this project yet.</p>
        {canEdit && onUpload && (
          <Button type="button" variant="brand" size="sm" onClick={onUpload}>
            <Upload className="h-4 w-4 mr-1" /> Upload documents
          </Button>
        )}
      </div>
    );
  }

  return (
    <EvidenceBase
      projectId={projectId}
      projectModel={projectModel}
      isAdvancedRag={project?.rag_mode === "advanced"}
      ownership={PROJECT_OWNERSHIP_CONFIG}
      showOwnershipFilter={false}
      allowEdit={canEdit}
      allowDelete={canEdit}
      height="70vh"
      onOpenDocument={(doc) => openDocument({ doc })}
      onSaved={refresh}
      onDeleted={refresh}
      onError={(e) => toast({ title: "Evidence Base", description: e.message, variant: "destructive" })}
      toolbarActions={
        <>
          {canEdit && onUpload && (
            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={onUpload}>
              <Upload className="h-4 w-4" /> Upload documents
            </Button>
          )}
          {toolbarActions}
        </>
      }
    />
  );
};

export default ProjectEvidenceBase;
